// Day.js
import dayjs from "dayjs";
// Fhir front library
import { SearchableTable } from "@fyrstain/hl7-front-library";
// Font awesome
import { faEye } from "@fortawesome/free-solid-svg-icons";
// Navigation
import { useNavigate } from "react-router-dom";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback } from "react";
// Fhir
import { Appointment } from "fhir/r4";
// Component
import AcesoPage from "src/components/AcesoPage/AcesoPage";

const AppointmentsPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const onDetails = useCallback(
    (id: string) => {
      navigate(`/Pathway/Appointments/${id}`);
    },
    [navigate],
  );
  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    console.error(
      "Appointment error :",
      error ?? "Network or FHIR error",
    );
  }, []);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  function getType(appointment: Appointment): string {
    return (
      appointment.serviceType?.[0]?.text ??
      appointment.serviceType?.[0]?.coding?.[0]?.display ??
      appointment.serviceType?.[0]?.coding?.[0]?.code ??
      appointment.appointmentType?.text ??
      appointment.appointmentType?.coding?.[0]?.display ??
      appointment.appointmentType?.coding?.[0]?.code ??
      i18n.t("general.notavailable")
    );
  }

  function getPatient(appointment: Appointment): string {
    const patientParticipant = appointment.participant?.find(
      (participant) =>
        participant.actor?.reference?.startsWith("Patient/") ||
        participant.actor?.type === "Patient",
    );

    return (
      patientParticipant?.actor?.display ??
      patientParticipant?.actor?.identifier?.value ??
      patientParticipant?.actor?.reference ??
      i18n.t("general.notavailable")
    );
  }

  function formatDate(value?: string): string {
    if (!value) {
      return i18n.t("general.notavailable");
    }

    return dayjs(value).format("DD/MM/YYYY HH:mm");
  }

  function getStatus(appointment: Appointment): string {
    if (!appointment.status) {
      return i18n.t("general.notavailable");
    }

    return i18n.t(`appointment.status.${appointment.status}`, {
      defaultValue: appointment.status,
    });
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <AcesoPage
        loading={false}
        titleKey="pathway.tabs.appointments"
        needsLogin={true}
      >
    <SearchableTable
      searchCriteriaProperties={{
        title: i18n.t("title.searchcriteria"),
        submitButtonLabel: i18n.t("button.search"),
        resetButtonLabel: i18n.t("button.reset"),
        language: i18n.t,
        fixedParameters: {
          _sort: "-date",
        },
        inputs: [
          {
            label: i18n.t("label.patient"),
            type: "text",
            searchParamsName: "patient.name",
            placeholder: i18n.t("placeholder.patientname"),
          },
          {
            label: i18n.t("label.date"),
            type: "date",
            searchParamsName: "date",
          },
          {
            label: i18n.t("label.status"),
            type: "select",
            searchParamsName: "status",
            options: [
              {
                label: i18n.t("appointment.status.proposed"),
                value: "proposed",
              },
              {
                label: i18n.t("appointment.status.pending"),
                value: "pending",
              },
              {
                label: i18n.t("appointment.status.booked"),
                value: "booked",
              },
              {
                label: i18n.t("appointment.status.arrived"),
                value: "arrived",
              },
              {
                label: i18n.t("appointment.status.fulfilled"),
                value: "fulfilled",
              },
              {
                label: i18n.t("appointment.status.cancelled"),
                value: "cancelled",
              },
              {
                label: i18n.t("appointment.status.noshow"),
                value: "noshow",
              },
              {
                label: i18n.t("appointment.status.entered-in-error"),
                value: "entered-in-error",
              },
              {
                label: i18n.t("appointment.status.checked-in"),
                value: "checked-in",
              },
              {
                label: i18n.t("appointment.status.waitlist"),
                value: "waitlist",
              },
            ],
          },
        ],
      }}
      paginatedTableProperties={{
        columns: [
          {
            header: i18n.t("label.patient"),
            dataField: "patient",
            width: "22%",
          },
          {
            header: i18n.t("label.startdate"),
            dataField: "startDate",
            width: "17%",
          },
          {
            header: i18n.t("label.enddate"),
            dataField: "endDate",
            width: "17%",
          },
          {
            header: i18n.t("label.status"),
            dataField: "status",
            width: "14%",
          },
          {
            header: i18n.t("label.type"),
            dataField: "type",
            width: "22%",
          },
        ],
        action: [
          {
            icon: faEye,
            onClick: onDetails,
          },
        ],
        mapResourceToData: (resource: Appointment) => ({
          id: resource.id,
          patient: getPatient(resource),
          startDate: formatDate(resource.start),
          endDate: formatDate(resource.end),
          status: getStatus(resource),
          type: getType(resource),
        }),
        searchProperties: {
          serverUrl: process.env.REACT_APP_FHIR_URL as string,
          resourceType: "Appointment",
        },
        onError,
      }}
    />
    </AcesoPage>
  );
};

export default AppointmentsPage;